import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/compat/router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { space } from 'styled-system';
import SignUpForm from 'components/forms/sign-up-form';
import useUI from 'src/hooks/use-ui';
import useUser from 'src/hooks/use-user';
import { Divider } from 'src/components/divider';
import { AppleLogin, GoogleLogin } from 'src/components/social-login';
import { useRedirectUrlForMobileEcomm } from 'src/components/modals/login/redirect-mobile-ecomm';
import { isReferralsRoute as isReferralsRouteUtils } from 'src/components/loyalty/rewards/dpos-utils';

export type SignupContentProps = {
  onClose?: () => void;
  analyticsEventLabel?: string;
  redirectUrl?: string | null;
};

export function SignupContent({ onClose, analyticsEventLabel, redirectUrl = null }: SignupContentProps): JSX.Element {
  const { token } = useUser();
  const router = useRouter();
  const query = router?.query ?? {};
  const flags = useFlags();
  const { isMobileEcommApp } = useUI();
  const mobileEcommRedirectUrl = useRedirectUrlForMobileEcomm({ query, token });
  const publicContextSessionId = Array.isArray(query['session-id'])
    ? query['session-id'][0]
    : query['session-id'] ?? null;

  useEffect(() => {
    if (mobileEcommRedirectUrl) {
      window.location.href = mobileEcommRedirectUrl;
    }
  }, [mobileEcommRedirectUrl]);

  const isReferralsRoute = router ? isReferralsRouteUtils(router) : false;
  const isShowReferralsEnabled = flags['pos.customers.refer-a-friend.rollout'] ?? false;

  return (
    <ContentContainer pb={isMobileEcommApp ? 30 : 40}>
      {isShowReferralsEnabled && isReferralsRoute && (
        <SubHeaderText>Enjoy loyalty perks, saved preferences, and a smoother shopping experience.</SubHeaderText>
      )}
      <SocialLogins>
        <GoogleLogin
          redirectUrl={redirectUrl ?? window.location.href}
          publicContextSessionId={publicContextSessionId}
        />
        <AppleLogin redirectUrl={redirectUrl ?? window.location.href} publicContextSessionId={publicContextSessionId} />
      </SocialLogins>

      <Divider text='or manually enter your information' />

      <SignUpForm
        onClose={() => {
          if (redirectUrl) {
            window.location.href = redirectUrl;
          }

          onClose?.();
        }}
        analyticsEventLabel={analyticsEventLabel}
        publicContextSessionId={publicContextSessionId}
      />
    </ContentContainer>
  );
}

export const ContentContainer = styled.div`
  padding: 0 40px;
  height: 100%;

  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    padding-right: 34px;
    padding-left: 34px;
  }

  ${space}
`;

export const SubHeaderText = styled.p`
  font-size: 14px;
  font-weight: 200;
  color: #454e50;
  text-align: left;
  margin: 10px 2px 24px 2px;
`;

const SocialLogins = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;
